import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import SegmentationExplorerCore from '../components/segmentation_explorer/SegmentationExplorerCore';
import { getSegmentName } from '../utils/segmentUtils';

const SegmentationExplorer = () => {
  // Core state for the segment explorer
  const [selectedSegment, setSelectedSegment] = useState('urban');
  const [selectedStations, setSelectedStations] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [viewType, setViewType] = useState('data');
  const [timeFrame, setTimeFrame] = useState('monthly');
  const [categoryFilter, setCategoryFilter] = useState('fuel');
  const [selectedMetric, setSelectedMetric] = useState('volume');
  
  // Additional filter state
  const [filters, setFilters] = useState({
    region: 'all',
    format: 'all',
    performance: 'all',
    growth: 'all'
  });
  
  // State for demographic data by segment
  const [demographicsData, setDemographicsData] = useState({
    urban: [
      { name: 'Local Residential', value: 30 },
      { name: 'Commercial', value: 45 },
      { name: 'Commuters', value: 25 }
    ],
    highway: [
      { name: 'Local Residential', value: 15 },
      { name: 'Commercial', value: 60 },
      { name: 'Travelers', value: 25 }
    ],
    rural: [
      { name: 'Local Residential', value: 55 },
      { name: 'Agricultural', value: 35 },
      { name: 'Travelers', value: 10 }
    ],
    suburban: [
      { name: 'Local Residential', value: 50 },
      { name: 'Commercial', value: 30 },
      { name: 'Commuters', value: 20 }
    ]
  });
  
  // State for segment performance metrics
  const [customerSatisfaction] = useState({
    urban: 92,
    highway: 85,
    rural: 89,
    suburban: 87
  });
  
  const [clvData] = useState({
    urban: 8.7,
    highway: 6.4,
    rural: 5.3,
    suburban: 7.1
  });

  // State for share of wallet data
  const [shareOfWalletData] = useState({
    urban: 80,
    highway: 85,
    rural: 54,
    suburban: 65
  });

  // State for annual frequency data
  const [annualFrequencyData] = useState({
    urban: 50,
    highway: 24,
    rural: 22,
    suburban: 56
  });

  // Updated trend data to show growing trend for urban and declining trend for rural
  const [trendData, setTrendData] = useState([
    { month: 'Jan', urban: 5000, highway: 4200, rural: 3100, suburban: 3600 },
    { month: 'Feb', urban: 5150, highway: 4180, rural: 3050, suburban: 3610 },
    { month: 'Mar', urban: 5200, highway: 4150, rural: 3000, suburban: 3630 },
    { month: 'Apr', urban: 5280, highway: 4160, rural: 2930, suburban: 3650 },
    { month: 'May', urban: 5350, highway: 4140, rural: 2880, suburban: 3670 },
    { month: 'Jun', urban: 5420, highway: 4120, rural: 2820, suburban: 3690 }
  ]);

  // Sample station data - initialize with an empty array to prevent "not iterable" errors
  const [stationData, setStationData] = useState([]);

  // Initialize station data on component mount
  useEffect(() => {
    // Sample station data
    const sampleStations = [
      {
        id: 'URB001',
        name: 'Central Bangkok',
        volume: 5200,
        premium: 42,
        margin: 0.92,
        cafe: 320,
        carcare: 18500,
        clv: 9.2,
        loyalty: 48,
        status: 'above'
      },
      {
        id: 'HWY002',
        name: 'Highway North',
        volume: 6800,
        premium: 32,
        margin: 0.78,
        cafe: 180,
        carcare: 12000,
        clv: 7.5,
        loyalty: 36,
        status: 'on'
      },
      {
        id: 'RUR003',
        name: 'Chiang Mai Rural',
        volume: 3100,
        premium: 22,
        margin: 0.68,
        cafe: 120,
        carcare: 8500,
        clv: 4.8,
        loyalty: 28,
        status: 'below'
      },
      {
        id: 'SUB004',
        name: 'Phuket Town',
        volume: 4800,
        premium: 36,
        margin: 0.84,
        cafe: 260,
        carcare: 14500,
        clv: 8.2,
        loyalty: 42,
        status: 'above'
      }
    ];
    
    setStationData(sampleStations);
  }, []);

  // Function to handle segment selection
  const handleSegmentSelection = (segmentId) => {
    setSelectedSegment(segmentId);
    setSelectedStations([]);
    
    if (activeTab === 'overview') {
      setActiveTab('detail');
    }
  };
  
  // Function to handle station selection
  const handleStationSelection = (stationIds) => {
    // If stationIds is a single string, convert it to an array
    const stationList = Array.isArray(stationIds) ? stationIds : [stationIds];
    setSelectedStations(stationList);
    
    if (stationList.length > 0) {
      toast.success(`${stationList.length} station${stationList.length > 1 ? 's' : ''} selected`);
    }
  };
  
  // Function to handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterType]: value
    }));
    
    // Show a toast notification for filter changes
    const filterNames = {
      'region': 'Region',
      'format': 'Format',
      'performance': 'Performance',
      'growth': 'Growth'
    };
    
    toast.success(`${filterNames[filterType]} filter updated to ${value}`);
  };
  
  // Reset all filters to default
  const resetAllFilters = () => {
    setFilters({
      region: 'all',
      format: 'all',
      performance: 'all',
      growth: 'all'
    });
    
    toast.success('All filters reset');
  };

  // Function to handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  // Function to handle view type change (data/map)
  const handleViewTypeChange = (type) => {
    setViewType(type);
    toast.success(`View changed to ${type} view`);
  };
  
  // Function to update timeFrame - centralized to prevent duplicate notifications
  const handleTimeFrameChange = (value) => {
    // Only allow changing to 'monthly', ignore other values
    if (value !== 'monthly') {
      toast.error('Only Monthly time period is available');
      return;
    }
    
    if (timeFrame !== value) {
      setTimeFrame(value);
      toast.success('Time period set to Monthly');
    }
  };
  
  // Function to update categoryFilter
  const handleCategoryFilterChange = (value) => {
    setCategoryFilter(value);
    toast.success(`Category changed to ${value}`);
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <SegmentationExplorerCore 
        selectedSegment={selectedSegment}
        selectedStations={selectedStations}
        filters={filters}
        activeTab={activeTab}
        viewType={viewType}
        timeFrame={timeFrame}
        categoryFilter={categoryFilter}
        customerSatisfaction={customerSatisfaction}
        clvData={clvData}
        shareOfWalletData={shareOfWalletData}
        annualFrequencyData={annualFrequencyData}
        onSegmentSelect={handleSegmentSelection}
        onStationSelect={handleStationSelection}
        onFilterChange={handleFilterChange}
        onResetFilters={resetAllFilters}
        onTabChange={handleTabChange}
        onViewTypeChange={handleViewTypeChange}
        setTimeFrame={handleTimeFrameChange}
        setCategoryFilter={handleCategoryFilterChange}
        demographicsData={demographicsData[selectedSegment] || []}
        selectedMetric={selectedMetric}
        setSelectedMetric={setSelectedMetric}
        stationData={stationData}
        trendData={trendData}
      />
    </div>
  );
};

export default SegmentationExplorer;
